import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  ShieldAlert, ShieldCheck, Shield, AlertTriangle, TrendingDown,
  BarChart3, Activity, Target, RefreshCw, ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

function RiskGauge({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color = score < 33 ? '#10b981' : score < 66 ? '#f59e0b' : '#ef4444';
  const label = score < 33 ? 'LOW' : score < 66 ? 'MED' : 'HIGH';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size * 0.65 }}>
      <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`}>
        <path d={`M 6 ${size * 0.6} A ${radius} ${radius} 0 0 1 ${size - 6} ${size * 0.6}`}
          fill="none" stroke="#1e293b" strokeWidth="8" strokeLinecap="round" />
        <path d={`M 6 ${size * 0.6} A ${radius} ${radius} 0 0 1 ${size - 6} ${size * 0.6}`}
          fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: size * 0.1 }}>
        <span className="text-2xl font-black font-mono" style={{ color }}>{score.toFixed(0)}</span>
        <span className="text-[10px] font-mono font-bold" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

export default function RiskDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data: riskData } = await api.get('/api/risk/dashboard');
      setData(riskData);
    } catch (err) {
      console.error('Error loading risk data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 font-mono text-sm">{t('risk.loading')}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const p = data.portfolio || {};
  const limits = data.limits || {};

  // Radar chart data
  const radarData = [
    { metric: 'Drawdown', value: Math.min((p.max_drawdown / (limits.max_drawdown || 1)) * 100, 100), limit: 100 },
    { metric: 'Volatility', value: Math.min((p.volatility / (limits.max_volatility || 1)) * 100, 100), limit: 100 },
    { metric: 'Concentration', value: Math.min((p.max_concentration / (limits.max_concentration || 1)) * 100, 100), limit: 100 },
    { metric: 'Sharpe Inv', value: p.sharpe_ratio > 0 ? Math.min(100 - (p.sharpe_ratio / 2) * 100, 100) : 80, limit: 100 },
    { metric: 'Diversificacion', value: p.num_agents > 0 ? Math.max(0, 100 - p.num_agents * 15) : 100, limit: 100 },
  ];

  // Exposure pie data
  const exposureColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div data-testid="risk-dashboard">
      <main className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-xl font-black text-white" style={{ fontFamily: 'Chivo' }}>{t('risk.title')}</h1>
              <p className="text-xs text-slate-400 font-mono">
                Profile: <span className="text-emerald-400 uppercase font-bold">{data.risk_profile}</span>
                {' | '}Generated: {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : ''}
              </p>
            </div>
          </div>
          <button onClick={() => { setLoading(true); fetchData(); }}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-bold hover:bg-white/20 transition-colors"
            data-testid="refresh-risk-btn">
            <RefreshCw className="w-4 h-4" /> REFRESH
          </button>
        </div>

        {/* Alerts */}
        {data.alerts?.length > 0 && (
          <div className="space-y-2" data-testid="risk-alerts">
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 border text-sm ${
                a.level === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                'bg-amber-500/10 border-amber-500/30 text-amber-400'
              }`}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="font-mono text-xs font-bold mr-2">{a.metric}:</span>
                <span className="text-xs">{a.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Top Row: Risk Score + Metrics + Radar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Risk Score Gauge */}
          <div className="bg-[#090B0F] border border-white/10 p-6 flex flex-col items-center justify-center" data-testid="risk-score-card">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-3">{t('risk.riskScore')}</p>
            <RiskGauge score={p.risk_score} size={160} />
            <p className="text-sm text-slate-400 mt-3">
              {p.risk_level === 'Low' && <span className="text-emerald-400 font-bold">{t('risk.lowRisk')}</span>}
              {p.risk_level === 'Medium' && <span className="text-amber-400 font-bold">{t('risk.modRisk')}</span>}
              {p.risk_level === 'High' && <span className="text-red-400 font-bold">{t('risk.highRisk')}</span>}
            </p>
          </div>

          {/* Key Metrics */}
          <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('risk.keyMetrics')}</h3>
            {[
              { label: 'Max Drawdown', value: `${p.max_drawdown?.toFixed(2)}%`, limit: `< ${limits.max_drawdown}%`, ok: p.max_drawdown <= limits.max_drawdown, icon: TrendingDown },
              { label: 'Volatility (Ann.)', value: `${p.volatility?.toFixed(2)}%`, limit: `< ${limits.max_volatility}%`, ok: p.volatility <= limits.max_volatility, icon: Activity },
              { label: 'Sharpe Ratio', value: p.sharpe_ratio?.toFixed(2), limit: `> ${limits.min_sharpe}`, ok: p.sharpe_ratio >= limits.min_sharpe, icon: Target },
              { label: 'Max Concentracion', value: `${p.max_concentration?.toFixed(1)}%`, limit: `< ${limits.max_concentration}%`, ok: p.max_concentration <= limits.max_concentration, icon: BarChart3 },
            ].map(m => (
              <div key={m.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <m.icon className={`w-4 h-4 ${m.ok ? 'text-emerald-500' : 'text-red-400'}`} />
                  <span className="text-xs text-slate-400">{m.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-mono font-bold ${m.ok ? 'text-white' : 'text-red-400'}`}>{m.value}</span>
                  <span className="text-[10px] text-slate-600 font-mono">{m.limit}</span>
                  {m.ok ? <ShieldCheck className="w-4 h-4 text-emerald-500" /> : <ShieldAlert className="w-4 h-4 text-red-400" />}
                </div>
              </div>
            ))}
          </div>

          {/* Radar Chart */}
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-2">{t('risk.riskRadar')}</h3>
            <div className="h-[200px]" data-testid="risk-radar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#1e293b" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} />
                  <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                  <Radar dataKey="limit" stroke="#334155" fill="none" strokeWidth={1} strokeDasharray="4 4" />
                  <Radar dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Market Exposure + Correlations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Market Exposure */}
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4">{t('risk.marketExposure')}</h3>
            {data.market_exposure?.length > 0 ? (
              <div className="space-y-3">
                {data.market_exposure.map((e, i) => (
                  <div key={e.market}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-white">{e.market}</span>
                      <span className="text-xs font-mono text-slate-400">${e.amount?.toLocaleString()} ({e.pct}%)</span>
                    </div>
                    <div className="h-2 bg-[#030508] overflow-hidden">
                      <div className="h-full transition-all duration-500"
                        style={{ width: `${e.pct}%`, backgroundColor: exposureColors[i % exposureColors.length] }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600 text-xs font-mono text-center py-4">{t('risk.noExposure')}</p>
            )}
          </div>

          {/* Correlation Matrix */}
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4">{t('risk.agentCorrelations')}</h3>
            {data.correlations?.length > 0 ? (
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {data.correlations.sort((a, b) => b.correlation - a.correlation).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-white">{c.agent_a}</span>
                      <ChevronRight className="w-3 h-3 text-slate-600" />
                      <span className="text-xs font-mono text-white">{c.agent_b}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-[#030508] overflow-hidden">
                        <div className="h-full"
                          style={{
                            width: `${c.correlation * 100}%`,
                            backgroundColor: c.level === 'high' ? '#ef4444' : c.level === 'medium' ? '#f59e0b' : '#10b981',
                          }} />
                      </div>
                      <span className={`text-xs font-mono font-bold ${
                        c.level === 'high' ? 'text-red-400' : c.level === 'medium' ? 'text-amber-400' : 'text-emerald-400'
                      }`}>{c.correlation.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600 text-xs font-mono text-center py-4">{t('risk.need2Agents')}</p>
            )}
          </div>
        </div>

        {/* Agent Risk Table */}
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('risk.riskByAgent')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                  <th className="text-left p-3">{t('common.agent')}</th>
                  <th className="text-left p-3">{t('common.market')}</th>
                  <th className="text-right p-3">{t('common.invested')}</th>
                  <th className="text-right p-3">{t('common.concentration')}</th>
                  <th className="text-right p-3">{t('common.drawdown')}</th>
                  <th className="text-right p-3">{t('common.volatility')}</th>
                  <th className="text-right p-3">{t('common.sharpe')}</th>
                  <th className="text-right p-3">{t('common.winRate')}</th>
                  <th className="text-right p-3">{t('risk.riskScoreCol')}</th>
                </tr>
              </thead>
              <tbody>
                {data.agents?.map(a => (
                  <tr key={a.agent_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono font-bold text-white">{a.agent_id} <span className="text-slate-500 text-xs font-normal">{a.agent_name}</span></td>
                    <td className="p-3 text-xs"><span className="px-2 py-0.5 bg-white/10 text-slate-300">{a.market_type}</span></td>
                    <td className="p-3 text-right text-white font-mono">${a.invested?.toLocaleString()}</td>
                    <td className="p-3 text-right font-mono text-white">{a.concentration?.toFixed(1)}%</td>
                    <td className={`p-3 text-right font-mono ${a.max_drawdown > limits.max_drawdown ? 'text-red-400' : 'text-white'}`}>
                      {a.max_drawdown?.toFixed(2)}%
                    </td>
                    <td className={`p-3 text-right font-mono ${a.volatility > limits.max_volatility ? 'text-red-400' : 'text-white'}`}>
                      {a.volatility?.toFixed(2)}%
                    </td>
                    <td className={`p-3 text-right font-mono ${a.sharpe_ratio < limits.min_sharpe ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {a.sharpe_ratio?.toFixed(2)}
                    </td>
                    <td className="p-3 text-right font-mono text-white">{a.win_rate?.toFixed(1)}%</td>
                    <td className="p-3 text-right">
                      <span className={`px-2 py-1 text-xs font-mono font-bold ${
                        a.risk_score < 33 ? 'bg-emerald-500/20 text-emerald-400' :
                        a.risk_score < 66 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{a.risk_score?.toFixed(0)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Risk Limits Reference */}
        <div className="bg-[#090B0F] border border-white/10 p-6">
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-3">
            Profile Limits <span className="text-emerald-400 uppercase">{data.risk_profile}</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#030508] border border-white/5 p-3">
              <p className="text-[10px] text-slate-500 font-mono uppercase">{t('risk.maxDrawdown')}</p>
              <p className="text-lg font-bold text-white">{limits.max_drawdown}%</p>
            </div>
            <div className="bg-[#030508] border border-white/5 p-3">
              <p className="text-[10px] text-slate-500 font-mono uppercase">{t('risk.maxVolatility')}</p>
              <p className="text-lg font-bold text-white">{limits.max_volatility}%</p>
            </div>
            <div className="bg-[#030508] border border-white/5 p-3">
              <p className="text-[10px] text-slate-500 font-mono uppercase">{t('risk.minSharpe')}</p>
              <p className="text-lg font-bold text-white">{limits.min_sharpe}</p>
            </div>
            <div className="bg-[#030508] border border-white/5 p-3">
              <p className="text-[10px] text-slate-500 font-mono uppercase">{t('risk.maxConcentration')}</p>
              <p className="text-lg font-bold text-white">{limits.max_concentration}%</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
